import moment from 'moment';
import { DataUtils } from '../utils/data-utils';

type TimezonedDateConstructorArgs =
    | []
    | [number | string | Date | TimezonedDate | ApiDate]
    | [number, number, number?, number?, number?, number?, number?];

/**
 * @class TimezonedDate
 * This class is an extension of the JavaScript date class, that allows easier handling of timezones and
 * timezone related operations.
 * For this purpose it implements the date interface and behaves exactly like a date object for all operations.
 * In addition to that it provides a specific way to be constructed correctly from API date values (utc date + offset).
 * It offers additional operations:
 * - toTimezonedISOString() - gets an ISO string not converted to UTC including timezone
 *
 *
 * Complexity of date handling and scenarios. The dates shall be interpreted with different behaviors:
 *
 * 1. UTC: The true universal time of the date.
 * 2. Local time: The local date and time of the local computer (UTC +/- offset)
 * 3. Tracked local time: This is a special case. Date objects only support 1. and 2. To achieve this, a date
 *    conversion needs to be performed. Example:
 *    - John tracks with his local time (UTC +04:00) an entry of '2025-08-15T14:30:00'.
 *      This will result in: '2025-08-15T10:30:00Z' and '+04:00'
 *    - Jane now loads this entry in her local time (UTC +02:00).
 *      This will result in a new Date object constructed from the UTC time '2025-08-15T10:30:00Z'.
 *      The date will be automatically converted to her local time, so that Jane see '2025-08-15T12:30:00'
 *    - In order to see the time the same way John has tracked it (local date and time of John), the date
 *      needs to be converted by subtracting the timezone difference -('+04:00' - '+02:00') = +02:00
 *      '2025-08-15T12:30:00' + +02:00 = '2025-08-15T14:30:00'
 *
 * Note: Implementing Date interface is not sufficient for passing Date checks in Angular components. Therefore,
 *       the class extends Date. Drawback is, that in case Date interface is changed, this will not be detected
 *       by the compiler.
 */
export class TimezonedDate extends Date {
    /**
     * Creates a new TimezonedDate object from a Date object.
     * If the date is already a TimezonedDate object, the original object is returned.
     * @param date The date to be converted.
     * @param offsetToKeep Optional parameter to apply the provided offset to the new TimezonedDate object. TODO: not implemented yet!
     * @returns A new TimezonedDate object representing the same date as the provided date or the provided TimezonedDate object itself (same reference).
     */
    public static fromDate(date: Date, offsetToKeep: string | null = null): TimezonedDate {
        if (date instanceof TimezonedDate && offsetToKeep === null) {
            return date;
        }
        return new TimezonedDate(date);
    }

    public static fromApi(utcDate: string, utcOffset: string): TimezonedDate {
        const apiDate = new ApiDate(utcDate, utcOffset);
        const tzDate = new TimezonedDate(apiDate);
        if (isNaN(tzDate.getTime())) {
            console.error(`TimezonedDate.fromApi() received invalid date: "${utcDate}" with offset: "${utcOffset}"`);
        }
        return tzDate;
    }

    public static override now(): number {
        return Date.now();
    }

    public static tzNow(timeMod?: number, unit?: moment.DurationInputArg2): TimezonedDate {
        if (!timeMod || timeMod === 0) {
            return new TimezonedDate();
        }
        const date = moment().add(timeMod, unit).toDate();
        return new TimezonedDate(date);
    }

    /**
     * Converts a Date object to a TimezonedDate object.
     * Safe, non-throwing version of toTimezonedDate().
     *
     *  **Attention:**
     *  - If an invalid date is passed, it is returned unchanged.
     *  - In that case, the runtime return value is actually `TimezonedDate | Date`,
     *    even though the declared return type is `TimezonedDate`.
     *  - This behavior is intentional and exists as a convenience for UI/date-picker
     *    flows where manually entered values may be temporarily invalid.
     *
     * @param date The date to be converted, if needed.
     * @returns A TimezonedDate representing the same point in time, or the original invalid Date unchanged.
     */
    public static toTimezonedDateSafe(date: Date | TimezonedDate): TimezonedDate {
        return this._toTimezonedDate(date, false);
    }

    /**
     * Converts a Date object to a TimezonedDate object.
     * If the date is already a TimezonedDate object, the original object is returned.
     * If the date is invalid, an error is thrown.
     *
     * @param date The date to be converted, if needed.
     * @returns A TimezonedDate representing the same point in time.
     * @throws Error If the provided date is invalid.
     */
    public static toTimezonedDate(date: Date | TimezonedDate): TimezonedDate {
        return this._toTimezonedDate(date, true);
    }

    private static _toTimezonedDate(date: Date | TimezonedDate, throwOnInvalid: boolean): TimezonedDate {
        if (date instanceof TimezonedDate) {
            return date;
        }
        if (!DataUtils.isValidDate(date)) {
            if (throwOnInvalid) {
                throw new Error(`Invalid date: ${date}`);
            }
            // deliberate passthrough for invalid UI/date-picker input
            // with date pickers, this is a plain Date rather than a TimezonedDate,
            // even if the picker was initialized with a TimezonedDate
            return date as TimezonedDate;
        }
        return TimezonedDate.fromDate(date);
    }

    private _date: Date;
    private _apiDate: ApiDate;
    private _backup?: TimezonedDate;

    constructor(...args: TimezonedDateConstructorArgs) {
        const hasArgs = args && args.length > 0;
        const superArgs =
            hasArgs && args[0] instanceof ApiDate
                ? args[0].utcDate
                : hasArgs && args[0] instanceof TimezonedDate
                  ? args[0]._date
                  : hasArgs
                    ? // eslint-disable-next-line @typescript-eslint/no-explicit-any -- with any this looks a lot cleaner... without it each tuple needs to be handled separately
                      new (Date as any)(...args)
                    : new Date();
        super(superArgs);
        if (hasArgs) {
            if (args[0] instanceof ApiDate) {
                // initialized with an ApiDate
                // Note: The date has to be constructed with the utc date time string without offset.
                //      The offset is only needed for displaying the local date value.
                //      Constructing with the offset instead of Z will result in constructing a local date,
                //      but the API date is UTC, not local.
                this._apiDate = args[0];
                this._date = new Date(this._apiDate.utcDate);
            } else if (args[0] instanceof TimezonedDate) {
                // clone case:
                const d = args[0];
                this._apiDate = new ApiDate(d._apiDate.utcDate, d._apiDate.utcOffset);
                this._date = structuredClone(d._date);
                this._backup = d._backup ? new TimezonedDate(d._backup) : undefined;
            } else {
                // initialized like a normal date object
                // eslint-disable-next-line @typescript-eslint/no-explicit-any -- with any this looks a lot cleaner... without it each tuple needs to be handled separately
                this._date = new (Date as any)(...args);
                this._apiDate = ApiDate.fromDate(this._date);
            }
        } else {
            this._date = new Date();
            this._apiDate = ApiDate.fromDate(this._date);
        }
    }

    // Delegate all Date methods to internalDate
    public override toString(): string {
        return this._date.toString();
    }

    public override toDateString(): string {
        return this._date.toDateString();
    }

    public override toTimeString(): string {
        return this._date.toTimeString();
    }

    public override toLocaleString(): string {
        return this._date.toLocaleString();
    }

    public override toLocaleDateString(): string {
        return this._date.toLocaleDateString();
    }

    public override toLocaleTimeString(): string {
        return this._date.toLocaleTimeString();
    }

    public override valueOf(): number {
        return this._date.valueOf();
    }

    public override getTime(): number {
        return this._date.getTime();
    }

    public override getFullYear(): number {
        return this._date.getFullYear();
    }

    public override getUTCFullYear(): number {
        return this._date.getUTCFullYear();
    }

    public override getMonth(): number {
        return this._date.getMonth();
    }

    public override getUTCMonth(): number {
        return this._date.getUTCMonth();
    }

    public override getDate(): number {
        return this._date.getDate();
    }

    public override getUTCDate(): number {
        return this._date.getUTCDate();
    }

    public override getDay(): number {
        return this._date.getDay();
    }

    public override getUTCDay(): number {
        return this._date.getUTCDay();
    }

    public override getHours(): number {
        return this._date.getHours();
    }

    public override getUTCHours(): number {
        return this._date.getUTCHours();
    }

    public override getMinutes(): number {
        return this._date.getMinutes();
    }

    public override getUTCMinutes(): number {
        return this._date.getUTCMinutes();
    }

    public override getSeconds(): number {
        return this._date.getSeconds();
    }

    public override getUTCSeconds(): number {
        return this._date.getUTCSeconds();
    }

    public override getMilliseconds(): number {
        return this._date.getMilliseconds();
    }

    public override getUTCMilliseconds(): number {
        return this._date.getUTCMilliseconds();
    }

    public override getTimezoneOffset(): number {
        return this._date.getTimezoneOffset();
    }

    public override setTime(time: number): number {
        return this._delegateAndUpdate('setTime', [time]);
    }

    /** Internal set time operation, that does not modify the ApiDate object. */
    private _setTime(time: number): number {
        return this._withUpdateParent(() => {
            return this._date.setTime(time);
        });
    }

    public override setMilliseconds(ms: number): number {
        return this._delegateAndUpdate('setMilliseconds', [ms]);
    }

    public override setUTCMilliseconds(ms: number): number {
        return this._delegateAndUpdate('setUTCMilliseconds', [ms]);
    }

    public override setSeconds(sec: number, ms?: number): number {
        return this._delegateAndUpdate('setSeconds', [sec, ms]);
    }

    public override setUTCSeconds(sec: number, ms?: number): number {
        return this._delegateAndUpdate('setUTCSeconds', [sec, ms]);
    }

    public override setMinutes(min: number, sec?: number, ms?: number): number {
        return this._delegateAndUpdate('setMinutes', [min, sec, ms]);
    }

    public override setUTCMinutes(min: number, sec?: number, ms?: number): number {
        return this._delegateAndUpdate('setUTCMinutes', [min, sec, ms]);
    }

    public override setHours(hours: number, min?: number, sec?: number, ms?: number): number {
        return this._delegateAndUpdate('setHours', [hours, min, sec, ms]);
    }

    public override setUTCHours(hours: number, min?: number, sec?: number, ms?: number): number {
        return this._delegateAndUpdate('setUTCHours', [hours, min, sec, ms]);
    }

    public override setDate(date: number): number {
        return this._withUpdateParent(() => {
            return this._date.setDate(date);
        });
    }

    public override setUTCDate(date: number): number {
        return this._withUpdateParent(() => {
            return this._date.setUTCDate(date);
        });
    }

    public override setMonth(month: number, date?: number): number {
        return this._delegateAndUpdate('setMonth', [month, date]);
    }

    public override setUTCMonth(month: number, date?: number): number {
        return this._delegateAndUpdate('setUTCMonth', [month, date]);
    }

    public override setFullYear(year: number, month?: number, date?: number): number {
        return this._delegateAndUpdate('setFullYear', [year, month, date]);
    }

    public override setUTCFullYear(year: number, month?: number, date?: number): number {
        return this._delegateAndUpdate('setUTCFullYear', [year, month, date]);
    }

    public override toUTCString(): string {
        return this._date.toUTCString();
    }

    public override toISOString(): string {
        return this._date.toISOString();
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- any type is the official API type
    public override toJSON(key?: any): string {
        return this._date.toJSON(key);
    }

    // Required for instanceof checks because Date implements Object
    get [Symbol.toStringTag](): string {
        return 'Date';
    }

    override [Symbol.toPrimitive](hint: 'default'): string;
    override [Symbol.toPrimitive](hint: 'string'): string;
    override [Symbol.toPrimitive](hint: 'number'): number;
    override [Symbol.toPrimitive](hint: string): string | number;
    override [Symbol.toPrimitive](hint: 'default' | 'string' | 'number' | string): string | number {
        // Mimic native Date behavior:
        // If hint is 'string' or 'default', return string coercion (toString)
        // If hint is 'number', return numeric coercion (valueOf)
        if (hint === 'string' || hint === 'default') {
            return this._date.toString();
        } else if (hint === 'number') {
            return this._date.valueOf();
        } else {
            // fallback same as default
            return this._date.toString();
        }
    }

    /**
     * Helper for correct handling optional parameters with date object.
     * When undefined is passed as value for an optional parameter, the date class will process it and result in
     * an invalid date object. Hence, the parameters must not even be passed in.
     *
     * Example:
     * ```
     *     setFullYear(year: number, month?: number, date?: number): number {
     *         return this.internalDate.setFullYear(year, month, date);
     *     }
     *
     *     // This will cause undefined to be passed to setFullYear, which results in an invalid date object.
     *     // Fixing every method in the way below, is not clean:
     *
     *     setFullYear(year: number, month?: number, date?: number): number {
     *         if (month === undefined && date === undefined) {
     *             return this.internalDate.setFullYear(year);
     *         } else if (date === undefined) {
     *             return this.internalDate.setFullYear(year, month);
     *         } else {
     *             return this.internalDate.setFullYear(year, month, date);
     *         }
     *     }
     *
     *     // This method fixes the issue when being used like this:
     *     setFullYear(year: number, month?: number, date?: number): number {
     *         return this.delegateMethod('setFullYear', [year, month, date]);
     *     }
     *
     * ```
     * @param methodName The method to be called on the internalDate object
     * @param args The arguments to be passed to it (undefined will be filtered).
     * @returns The return value of the called method.
     */
    private _delegateMethod<M extends keyof Date>(methodName: M, args: Parameters<Date[M]>): ReturnType<Date[M]> {
        const method = this._date[methodName]; // as (...args: Parameters<Date[M]>) => ReturnType<Date[M]>;
        if (!method) {
            throw new Error(`Method ${methodName.toString()} does not exist on internalDate`);
        }

        // Filter out trailing undefined arguments (optional params omitted)
        const filteredArgs = [...args];
        // Remove undefined values from the end (treated as absent)
        while (filteredArgs.length && filteredArgs[filteredArgs.length - 1] === undefined) {
            filteredArgs.pop();
        }

        // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type -- TODO FIX ME
        return (method as Function).apply(this._date, filteredArgs);
    }

    private _updateParent(): void {
        super.setTime(this._date.getTime());
        if (this._backup) {
            this._backup._updateParent();
        }
    }

    private _delegateAndUpdate<M extends keyof Date>(methodName: M, args: Parameters<Date[M]>): ReturnType<Date[M]> {
        const beforeUpdate = new Date(this._date);
        const retVal = this._delegateMethod(methodName, args);
        // update backup by adding the time difference of the change (avoids conversion)
        if (this._backup) {
            const diffMs = beforeUpdate.getTime() - this._date.getTime();
            this._backup._date = new Date(this._backup._date.getTime() - diffMs);
        }
        this._updateParent();
        this._updateApiDate();
        return retVal;
    }

    private _withUpdateParent<T>(fn: () => T): T {
        try {
            return fn();
        } finally {
            this._updateParent();
        }
    }

    private _updateApiDate(): void {
        this._apiDate.utcDate = this._date.toISOString();
        this._apiDate.updateIsoString();
        if (this._backup) {
            this._backup._updateApiDate();
        }
    }

    /***********************************************************
     ************  Additional methods  *************************
     ***********************************************************/

    /**
     * @returns an ISO-8601 string representing the date in the format: 2025-08-31T12:30:45.123+00:00
     */
    public toTimezonedISOString(): string {
        return this._apiDate.isoString;
    }

    /**
     * This method returns a timezoned ISO-8601 String like toTimezonedISOString().
     * But in contrast to that method, if the date was set to behave as a local date, but will always return
     * the timezoned string.
     *
     * @see toTimezonedISOString()
     * @returns an ISO-8601 string representing the date in the format: 2025-08-31T12:30:45.123+00:00
     */
    public toCanonicalISOString(): string {
        if (this._backup) {
            return this._backup._apiDate.isoString;
        }
        return this._apiDate.isoString;
    }

    /**
     * @returns the offset of the date in the format +/-05:00
     */
    public getUtcOffset(): string {
        return this._apiDate.utcOffset;
    }

    /**
     * This returns always the correct value for the API json no matter, if the object is set
     * to behave as local or not.
     */
    public toApiJson(): string {
        if (this._backup) {
            return this._backup._apiDate.utcDate;
        }
        return this._apiDate.utcDate;
    }

    public getApiUtcOffset(): string {
        if (this._backup) {
            return this._backup._apiDate.utcOffset;
        }
        return this._apiDate.utcOffset;
    }

    /**
     * This tells the object to behave as if it was a local date object of the tracked timezone.
     * This means, that the resulting date will be in the clients local time, but have the values of the tracking clients
     * local time. So when a user from Germany views a time record from India, it will display the indian time, not
     * the German time of that time records moment.
     *
     * It internally converts the date in a way, that the local time of the "tracked" date will be used
     * for the local time value of this date.
     *
     * Calling this method internally stores the original date and timezone information, but converts itself
     * to a date object, that has the same date time information, but in the local timezone.
     *
     * The "default" is false.
     *
     * @example:
     * ```
     * //a date was tracked at 12:34:56 on UTC +06:00
     * //the local timezone is UTC +04:00
     * const tzd = TimezonedDate.fromApi('2025-08-30T12:34:56Z', '+06:00');
     * console.log(tzd.toISOString());              // '2025-08-30T12:34:56.000Z'
     * console.log(tzd.toTimezonedISOString());     // '2025-08-30T18:34:56.000+06:00'
     * tzd.behaveAsTrackedLocal(true);
     * console.log(tzd.toISOString());              // '2025-08-30T14:34:56.000Z'
     * console.log(tzd.toTimezonedISOString());     // '2025-08-30T18:34:56.000+04:00'
     * //the date effectively shows the same value for the date and time in the local timezone as the tracked value was
     * ```
     *
     *
     *         const trackedUtc = '2025-09-04T12:34:56.000Z';
     *         const trackedOffset = '+07:00';
     *         const expectedTime = '2025-09-04 19:34:56';
     *
     * @param behaveAsTrackedLocal Sets the date to behave like a local date (true) or not (false). Default: false
     */
    public behaveAsTrackedLocal(behaveAsTrackedLocal: boolean): void {
        if ((this._backup && behaveAsTrackedLocal) || (!this._backup && !behaveAsTrackedLocal)) {
            // setting same state again, nothing to do
            return;
        }
        if (behaveAsTrackedLocal) {
            this._backup = this.clone();
            const localTrackedTimeWithoutOffset = this._apiDate.getLocalTrackedTime();
            const tmpDate = new TimezonedDate(localTrackedTimeWithoutOffset);
            this._apiDate = tmpDate._getApiDate();
            this._date = tmpDate._getDate();
            this._updateParent();
        } else {
            if (!this._backup) {
                const msg = 'ERROR: TimezonedDate.behaveAsLocal(false) - TimezonedDate internal state error.';
                console.error(msg);
                throw new Error(msg);
            }
            // revert operation, but apply any changes...
            this._apiDate = this._backup._apiDate;
            this._date = this._backup._date;
            this._backup = undefined;
            this._updateParent();
        }
    }

    /**
     * @returns True if the date is set to behave as local tracked, otherwise false.
     */
    public isBehavingAsTrackedLocal(): boolean {
        return !!this._backup;
    }

    /**
     * Returns a copy of the timezoned date object in local time, with the same date and time values:
     * If called on "2025-08-30T12:12:12+04:00" it will return "2025-08-30T12:12:12{+/-local offset}"
     * No conversion is performed.
     *
     * @returns the same date time object, ignoring its timezone as a local date object
     */
    public getAsLocalDate(): TimezonedDate {
        const utcDateWithoutOffset = this._apiDate.getDateWithoutOffset();
        return new TimezonedDate(utcDateWithoutOffset);
    }

    public clone(): TimezonedDate {
        return new TimezonedDate(this);
    }

    public format(format?: string): string {
        return moment(this._date).format(format);
    }

    /**
     * This method calculates the difference (duration) between two dates using moment()
     * This method is not safe to be called in combination with behaveAsTrackedLocal() use canonicalDiff() instead.
     * @see canonicalDiff()
     * @see behaveAsTrackedLocal()
     * @param diffDate MomentInput to compare the date to, e.g. TimezonedDate
     * @param unitOfTime A moment unit of time string, e.g. "minutes"
     * @param precise (optional, default false) When set to true, the method will return float values with
     *                full precision (e.g. 1.75 days), otherwise only full units (e.g. 1 day), floored
     * @returns The difference between the two dates as number
     */
    public diff(diffDate: moment.MomentInput, unitOfTime?: moment.unitOfTime.Diff, precise?: boolean): number {
        return moment(this._date).diff(diffDate, unitOfTime, precise);
    }

    /**
     * This method does the same as the diff() method, but ignores if the object is set to behave as a local tracked date.
     * As result, it will always return the correct difference according to its utc stored data.
     * While diff() may be called with any moment() compatible value, this method only supports to calculate the
     * difference between two TimezonedDate objects.
     * This method is safe to be called in combination with behaveAsTrackedLocal().
     * @see diff()
     * @see behaveAsTrackedLocal()
     * @param diffDate TimezonedDate to compare the date to
     * @param unitOfTime A moment unit of time string, e.g. "minutes"
     * @param precise (optional, default false) When set to true, the method will return float values with
     *                full precision (e.g. 1.75 days), otherwise only full units (e.g. 1 day), floored
     * @returns The difference between the two dates as number
     */
    public canonicalDiff(diffDate: TimezonedDate, unitOfTime?: moment.unitOfTime.Diff, precise?: boolean): number {
        const comp1 = this._backup ? this._backup : this;
        const comp2 = diffDate._backup ? diffDate._backup : diffDate;
        return moment(comp1._date).diff(comp2._date, unitOfTime, precise);
    }

    public getOffsetDiff(diffDate: TimezonedDate, unitOfTime: moment.unitOfTime.Base = 'minutes'): number {
        return TimezoneHelper.getOffsetDiff(this.getApiUtcOffset(), diffDate.getApiUtcOffset(), unitOfTime);
    }

    public dayOfYear(): number {
        return moment(this._date).dayOfYear();
    }

    /**
     * This method does not return a copy, but modifies the time of the object.
     * If you want a copied object, call .clone() before calling .add()
     *
     * @param amount The number of timeunit to be added
     * @param unit The time unit
     */
    public add(amount?: moment.DurationInputArg1, unit?: moment.DurationInputArg2): void {
        this._date = moment(this._date).add(amount, unit).toDate();
        const apiDate = this._apiDate.getDateWithoutOffset();
        const localDate = moment(apiDate).add(amount, unit).format('YYYY-MM-DDTHH:mm:ss.SSS');
        this._apiDate.utcDate = localDate + 'Z';
        this._apiDate.updateIsoString();
        this._updateParent();

        if (this._backup) {
            this._backup.add(amount, unit);
        }
    }

    /**
     * This assigns a new date value to the TimezonedDate.
     * In some cases, especially when components create Date objects, you may want to assign these dates values to
     * your TimezonedDate instance in order to preserve and correctly handle time zone information.
     * This method assigns the date, but does not change the utc offsets. If you want to create a completely
     * new TimezonedDate for the local timezone use `new TimezonedDate(yourDate)`.
     *
     * @example
     * ```
     * // from add-activity.component.ts
     * // the p-calendar component replace the TimezonedDate with a new instance of date rather than modifying it.
     * // therefore the value needs to be assigned to the existing TimezonedDate object
     *
     * if (this._selectedActivity) {
     *     this._selectedActivity.start.assign(this.dateFrom, true);
     *     this.dateFrom = this._selectedActivity.start;
     *     this._selectedActivity.end.assign(this.dateTo, true);
     *     this.dateTo = this._selectedActivity.end;
     * }
     * else {
     *     this.dateFrom = new TimezonedDate(this.dateFrom);
     *     this.dateTo = new TimezonedDate(this.dateTo);
     * }
     * ```
     *
     * @param date The date to be assigned.
     * @param expectLocal Is the assigned date expected to be one resulting from behaveAsTrackedLocal()
     */
    public assign(date: Date, expectLocal: boolean): void {
        if (!this._backup && !expectLocal) {
            // normal case - this is a local date and the input too
            this._setTime(date.getTime());
            this._apiDate.utcDate = TimezoneHelper.convertToOffset(date, this._apiDate.utcOffset).toISOString();
            this._apiDate.updateIsoString();
        } else if (this._backup && expectLocal) {
            this._setTime(date.getTime());
            this._apiDate.utcDate = TimezoneHelper.convertToOffset(date, this._apiDate.utcOffset).toISOString();
            this._apiDate.updateIsoString();
            // modify date for timezone
            const inputOffset = TimezoneHelper.getFormattedUTCOffset(date);
            const tzDiff = TimezoneHelper.getOffsetDiff(inputOffset, this._backup._apiDate.utcOffset, 'milliseconds');
            const modDate = new Date(date.getTime() + tzDiff);
            this._backup!.assign(modDate, true);
        } else if (!this._backup && expectLocal) {
            this._setTime(date.getTime());
            this._apiDate.utcDate = date.toISOString();
            this._apiDate.updateIsoString();
        } else {
            const msg =
                'Assigning a date that is considered to be in local track time to a TimezonedDate object, ' +
                'that is not behaving in local track time is not supported!';
            throw new Error('UNSUPPORTED OPERATION: ' + msg);
        }
    }

    /***********************************************************
     ************  Methods for debugging / testing *************
     ***********************************************************/

    public _getDate(): Date {
        return this._date;
    }

    public _getApiDate(): ApiDate {
        return this._apiDate;
    }

    public _debugInfo(): string {
        let info = `TimezonedDate.debugInfo():
 - _date: ${JSON.stringify(this._date)}
 - _apiDate: ${JSON.stringify(this._apiDate)}
 - _backup: (${JSON.stringify(this._backup)})`;
        if (this._backup) {
            info += `
     - _date: ${JSON.stringify(this._backup._date)}
     - _apiDate: ${JSON.stringify(this._backup._apiDate)}
`;
        }
        return info;
    }
}

// ////////// Helpers, constants, etc... not meant to be exported, except for tests... ///////////////

const ISO_FORMAT = 'YYYY-MM-DDTHH:mm:ss.SSSZ';
const ISO_FORMAT_WITHOUT_OFFSET = 'YYYY-MM-DDTHH:mm:ss.SSS';

export class TimezoneHelper {
    /**
     * Calculates the difference in minutes between two utc offsets.
     * @param offsetA UTC offset A in format '+02:00'
     * @param offsetB UTC offset B in format '+02:00'
     * @param unitOfTime The difference between offsetA and offsetB in minutes (offsetA - offsetB).
     */
    public static getOffsetDiff(
        offsetA: string,
        offsetB: string,
        unitOfTime: moment.unitOfTime.Base = 'minutes',
    ): number {
        const minutesA = moment.duration(offsetA).asMinutes();
        const minutesB = moment.duration(offsetB).asMinutes();
        const diffInMinutes = minutesA - minutesB;
        return moment.duration(diffInMinutes, 'minutes').as(unitOfTime);
    }

    /**
     * @param date The date to get the UTC offset for.
     * @returns A string representing the offset of `date` in format '+02:00'
     */
    public static getFormattedUTCOffset(date: Date): string {
        const offset = date.getTimezoneOffset();
        const sign = offset > 0 ? '-' : '+';
        const absOffset = Math.abs(offset);
        const hours = Math.floor(absOffset / 60)
            .toString()
            .padStart(2, '0');
        const minutes = (absOffset % 60).toString().padStart(2, '0');
        return `${sign}${hours}:${minutes}`;
    }

    /**
     *
     * @param localDate
     * @param originalOffset
     */
    public static convertToOffset(localDate: Date, originalOffset: string): Date {
        const iso = moment(localDate).format(ISO_FORMAT_WITHOUT_OFFSET);
        const isoWithOffset = iso + originalOffset;
        return moment(isoWithOffset).toDate();
    }

    public static getLocalTrackedTime(utcDate: string, utcOffset: string): string {
        return moment.utc(utcDate).utcOffset(utcOffset).format(ISO_FORMAT_WITHOUT_OFFSET);
    }
}

class ApiDate {
    public static fromDate(date: Date): ApiDate {
        return new ApiDate(date.toISOString(), TimezoneHelper.getFormattedUTCOffset(date));
    }

    utcDate: string;
    utcOffset: string;
    isoString: string;

    constructor(utcDate: string, utcOffset: string) {
        this.utcDate = utcDate;
        this.utcOffset = utcOffset;
        this.isoString = this.getIsoString();
    }

    public getDateWithoutOffset(): string {
        return this.utcDate.slice(0, this.utcDate.length - 1);
    }

    public getIsoString(): string {
        return moment.utc(this.utcDate).utcOffset(this.utcOffset, false).format(ISO_FORMAT);
        // '2025-09-04T12:34:56.000Z' and '+06:00' will result in '2025-09-04T18:34:56.000+06:00'
    }

    public updateIsoString(): void {
        this.isoString = this.getIsoString();
    }

    // returns the local tracked time without offset as a string
    public getLocalTrackedTime(): string {
        return TimezoneHelper.getLocalTrackedTime(this.utcDate, this.utcOffset);
    }
}
